import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DaysReport = ({ control, team, onUpdate }) => {
  const [showAddHelperModal, setShowAddHelperModal] = useState(false);
  const [newHelper, setNewHelper] = useState({ nome: '', diaria: 0 });
  const [editingDays, setEditingDays] = useState({});

  const updateDias = async (pessoaTipo, pessoaId, novosDias) => {
    try {
      await axios.post(`${API}/helper-controls/${control.id}/update-dias`, {
        pessoa_tipo: pessoaTipo,
        pessoa_id: pessoaId,
        dias: parseInt(novosDias) || 0
      });
      
      await onUpdate();
      toast.success('Dias atualizados!');
    } catch (error) {
      console.error('Erro ao atualizar dias:', error);
      toast.error('Erro ao atualizar dias');
    }
  };

  const addHelperAvulso = async () => {
    if (!newHelper.nome || newHelper.diaria <= 0) {
      toast.error('Preencha nome e diária válida');
      return;
    }

    try {
      await axios.post(`${API}/helper-controls/${control.id}/helper-avulso`, newHelper);
      toast.success('Helper avulso adicionado!');
      setShowAddHelperModal(false);
      setNewHelper({ nome: '', diaria: 0 });
      await onUpdate();
    } catch (error) {
      console.error('Erro ao adicionar helper:', error);
      toast.error('Erro ao adicionar helper');
    }
  };

  const removeHelperAvulso = async (helperId) => {
    if (!confirm('Remover este helper?')) return;

    try {
      await axios.delete(`${API}/helper-controls/${control.id}/helper-avulso/${helperId}`);
      toast.success('Helper removido!');
      await onUpdate();
    } catch (error) {
      console.error('Erro ao remover helper:', error);
      toast.error('Erro ao remover helper');
    }
  };

  // Calcular dias de cada pessoa
  const diasLider = control.dias_lider?.length || control.dias_trabalhados || 0;
  const helpersAvulsos = control.helpers_avulsos || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h4 className="font-semibold text-sm sm:text-base">👥 Dias Trabalhados por Pessoa</h4>
        <Button size="sm" variant="outline" onClick={() => setShowAddHelperModal(true)} className="text-xs">
          + Helper Avulso
        </Button>
      </div>

      {/* Líder */}
      <Card>
        <CardContent className="p-2 sm:p-3">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <div>
              <p className="font-medium text-sm">👨‍💼 {team?.lider_nome} (Líder)</p>
              <p className="text-xs text-gray-600">R${team?.lider_diaria || 0}/dia</p>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <Input
                type="number"
                min="0"
                value={editingDays['lider'] !== undefined ? editingDays['lider'] : diasLider}
                onChange={(e) => setEditingDays(prev => ({ ...prev, lider: e.target.value }))}
                onBlur={(e) => {
                  const valor = parseInt(e.target.value) || 0;
                  if (valor !== diasLider) {
                    updateDias('lider', null, valor);
                  }
                  setEditingDays(prev => {
                    const newState = { ...prev };
                    delete newState.lider;
                    return newState;
                  });
                }}
                className="w-14 sm:w-16 text-center text-sm"
              />
              <span className="text-xs sm:text-sm">dias</span>
              <span className="font-bold text-blue-600 text-sm sm:text-base whitespace-nowrap">
                R${(diasLider * (team?.lider_diaria || 0)).toFixed(2)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Helpers Fixos */}
      {team?.helpers && team.helpers.map(helper => {
        const diasHelper = control.dias_helpers?.[helper.id]?.length || control.dias_trabalhados || 0;
        return (
          <Card key={helper.id}>
            <CardContent className="p-2 sm:p-3">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <div>
                  <p className="font-medium text-sm">👷 {helper.nome}</p>
                  <p className="text-xs text-gray-600">R${helper.diaria}/dia</p>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Input
                    type="number"
                    min="0"
                    value={editingDays[`helper-${helper.id}`] !== undefined ? editingDays[`helper-${helper.id}`] : diasHelper}
                    onChange={(e) => setEditingDays(prev => ({ ...prev, [`helper-${helper.id}`]: e.target.value }))}
                    onBlur={(e) => {
                      const valor = parseInt(e.target.value) || 0;
                      if (valor !== diasHelper) {
                        updateDias('helper', helper.id, valor);
                      }
                      setEditingDays(prev => {
                        const newState = { ...prev };
                        delete newState[`helper-${helper.id}`];
                        return newState;
                      });
                    }}
                    className="w-14 sm:w-16 text-center text-sm"
                  />
                  <span className="text-xs sm:text-sm">dias</span>
                  <span className="font-bold text-green-600 text-sm sm:text-base whitespace-nowrap">
                    R${(diasHelper * helper.diaria).toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Helpers Avulsos */}
      {helpersAvulsos.map(helper => {
        const diasAvulso = helper.dias?.length || 0;
        return (
          <Card key={helper.id} className="border-orange-200">
            <CardContent className="p-2 sm:p-3">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <div>
                  <p className="font-medium text-sm">🔧 {helper.nome} <span className="text-xs text-orange-600">(Avulso)</span></p>
                  <p className="text-xs text-gray-600">R${helper.diaria}/dia</p>
                </div>
                <div className="flex items-center gap-2 justify-end flex-wrap">
                  <Input
                    type="number"
                    min="0"
                    value={editingDays[`avulso-${helper.id}`] !== undefined ? editingDays[`avulso-${helper.id}`] : diasAvulso}
                    onChange={(e) => setEditingDays(prev => ({ ...prev, [`avulso-${helper.id}`]: e.target.value }))}
                    onBlur={(e) => {
                      const valor = parseInt(e.target.value) || 0;
                      if (valor !== diasAvulso) {
                        updateDias('avulso', helper.id, valor);
                      }
                      setEditingDays(prev => {
                        const newState = { ...prev };
                        delete newState[`avulso-${helper.id}`];
                        return newState;
                      });
                    }}
                    className="w-14 sm:w-16 text-center text-sm"
                  />
                  <span className="text-xs sm:text-sm">dias</span>
                  <span className="font-bold text-orange-600 text-sm sm:text-base whitespace-nowrap">
                    R${(diasAvulso * helper.diaria).toFixed(2)}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 text-xs px-2"
                    onClick={() => removeHelperAvulso(helper.id)}
                  >
                    🗑️
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Modal: Adicionar Helper Avulso */}
      <Dialog open={showAddHelperModal} onOpenChange={setShowAddHelperModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>🔧 Adicionar Helper Avulso</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Helper</Label>
              <Input
                value={newHelper.nome}
                onChange={(e) => setNewHelper(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: Carlos (temporário)"
              />
            </div>
            <div>
              <Label>Diária (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={newHelper.diaria}
                onChange={(e) => setNewHelper(prev => ({ ...prev, diaria: parseFloat(e.target.value) || 0 }))}
                placeholder="Ex: 100.00"
              />
            </div>
            <p className="text-sm text-gray-600">
              ℹ️ Após adicionar, você poderá editar a quantidade de dias que este helper trabalhou
            </p>
            <div className="flex gap-2">
              <Button onClick={addHelperAvulso} className="flex-1">Adicionar</Button>
              <Button variant="outline" onClick={() => setShowAddHelperModal(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DaysReport;
